import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clapperboard, Building2, Layers, Zap, ChevronDown, Play, Check } from "lucide-react";

const ROLES = [
  { path: "/demo", Icon: Clapperboard, title: "I'm a Project Manager", sub: "A day in the life of a PM · ~4 min", short: "A PM's day" },
  { path: "/demo/pmo", Icon: Building2, title: "I lead a PMO / portfolio", sub: "The portfolio layer tour · ~3 min", short: "A PMO leader's day" },
  { path: "/demo/features", Icon: Layers, title: "Show me every feature", sub: "The full feature tour · ~4 min", short: "Feature tour" },
  { path: "/demo/highlights", Icon: Zap, title: "I have 2 minutes", sub: "Just the biggest moments", short: "Highlights" },
];

const ROLE_KEYS = { "/demo": "journey", "/demo/pmo": "pmo", "/demo/features": "features", "/demo/highlights": "highlights" };

export const DemoPicker = () => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(() => { try { return localStorage.getItem("demo_role"); } catch { return null; } });
  const ref = useRef(null);
  const savedRole = ROLES.find((r) => r.path === saved);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (path) => {
    try { localStorage.setItem("demo_role", path); } catch {}
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/analytics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ video: ROLE_KEYS[path] || "journey", event: "role" }),
      keepalive: true,
    }).catch(() => {});
    setSaved(path);
    nav(path);
  };

  return (
    <div className="relative" ref={ref}>
      {savedRole ? (
        <div className="flex items-stretch">
          <Button size="lg" variant="outline"
            className="demo-flash gap-2 h-12 px-5 rounded-r-none border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white"
            onClick={() => nav(savedRole.path)} data-testid="hero-watch-demo-button">
            <Play className="h-4 w-4" /> Watch: {savedRole.short}
          </Button>
          <Button size="lg" variant="outline"
            className="h-12 px-2.5 rounded-l-none border-l-0 border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white"
            onClick={() => setOpen((o) => !o)} title="Pick a different demo" data-testid="hero-demo-picker-toggle">
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </Button>
        </div>
      ) : (
        <Button size="lg" variant="outline"
          className="demo-flash gap-2 h-12 px-6 border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white"
          onClick={() => setOpen((o) => !o)} data-testid="hero-watch-demo-button">
          <Clapperboard className="h-4 w-4" /> Watch a demo <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      )}
      {open && (
        <div className="absolute z-30 mt-2 w-80 rounded-xl border border-white/15 bg-[#0b1226]/95 backdrop-blur-md p-2 shadow-[0_20px_60px_rgba(0,0,0,0.5)] animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="demo-picker-menu">
          <p className="px-3 pt-2 pb-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-[#8fb2ff]">Pick your role</p>
          {ROLES.map((r) => (
            <button key={r.path} onClick={() => pick(r.path)}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-white/10 transition-colors"
              data-testid={`demo-picker-${r.path.replace(/\//g, "-").slice(1) || "demo"}`}>
              <span className="h-9 w-9 shrink-0 rounded-lg bg-[#0044FF]/25 border border-[#7ea6ff]/30 flex items-center justify-center">
                <r.Icon className="h-4 w-4 text-[#8fb2ff]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-white leading-tight">{r.title}</span>
                <span className="block text-xs text-slate-400 mt-0.5">{r.sub}</span>
              </span>
              {saved === r.path && <Check className="h-4 w-4 shrink-0 text-[#8fb2ff]" data-testid="demo-picker-saved-check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
